import type { PlannedSurvey, TeamSurveys } from '../../api/queries';
import type { CustomSurveyResponse, ScheduledCustomSurvey } from '../../api/customSurveys';
import {
  WorkEnvironmentSurvey,
  WorkEnvironmentSurveyStatus,
} from '../../api/workEnvironmentTypes';
import { Routes } from '../../enums/Routes';
import i18n from '../../i18n/config';
import { buildRoute } from '../../utils/routeBuilder';
import { agreementOf, scoreStatusOf, type TeamAgreement } from '../psychological-safety/team-result';
import { SurveyStatusCardUtils } from '../../utils/SurveyStatusCardUtils/SurveyStatusCardUtils';
import { ScheduledSurveyPresenter } from '../custom-survey/scheduled-survey-presenter';
import {
  ResponseRateThresholds,
  SurveyDeadlineCalculator,
  SurveyStatusPresenter,
} from '../custom-survey/survey-status-presenter';

/**
 * Felles visningsmodell for undersøkelseslisten.
 * Hver modul har sin egen datakilde; funksjonene her gjør dem om til én liste (Split Phase),
 * slik at komponentene bare viser data og ikke kjenner forskjellene mellom modulene.
 */

export type SurveyModule = 'psychologicalSafety' | 'workEnvironment' | 'custom';

/** Fargene vi bruker på tagger og score (Designsystemet data-color). */
export type Tone = 'info' | 'success' | 'warning' | 'danger' | 'neutral';

export type SurveyListItem = {
  key: string;
  id: number;
  module: SurveyModule;
  title: string;
  startDate: Date;
  deadline: Date | null;
  deadlineInDays?: number;
  isOngoing: boolean;
  /** Antall svar er ukjent når modulen ikke vet hvor mange som er invitert. */
  responses: { count: number | null; expected: number | null; rate: number };
  status: { label: string; tone: Tone };
  score?: { value: number; tone: Tone };
  agreement?: TeamAgreement;
  note?: string;
  /** Relativ sti til svarskjemaet. Finnes bare mens undersøkelsen er åpen og lenken er kjent. */
  answerPath?: string;
  analysisRoute?: string;
  reportRoute?: string;
  detailsRoute?: string;
  resultsRoute?: string;
  canEditDeadline: boolean;
  canDelete: boolean;
};

export type PlannedSurveyListItem = {
  key: string;
  id: number;
  module: SurveyModule;
  title: string;
  scheduledTime: Date;
  frequencyLabel: string;
  editRoute: string;
  type: 'standard' | 'custom';
};

/** Den pågående målingen for psykologisk trygghet, med lenke (fra /api/surveys/ongoing/team/:teamId). */
export type OngoingSurveyLink = { id: number; uuid: string };

const MINIMUM_RESPONSES_FOR_SCORE = 3;

const toTone = (status: string): Tone =>
  status === 'info' || status === 'success' || status === 'warning' || status === 'danger'
    ? status
    : 'neutral';

const moduleKey = (module: SurveyModule, id: number) => `${module}-${id}`;

const answerPathFor = (route: Routes, uuid: string) => buildRoute(route, { guid: uuid });

export const fromPsychologicalSafety = (
  survey: TeamSurveys,
  teamId: number,
  ongoing: OngoingSurveyLink | null,
  now: Date = new Date(),
): SurveyListItem => {
  const {
    id,
    title,
    createdAt,
    deadlineInDays,
    isActive,
    analyzeStatus,
    responseRate,
    score,
    standardDeviation,
    numberOfRespondents,
    expectedParticipantsCount,
  } = survey;
  const t = i18n.t.bind(i18n);
  const isCompleted = analyzeStatus === 'COMPLETED';
  const hasNoResponses = SurveyStatusCardUtils.isInvalidForAnalyzing(isActive, responseRate);
  const isNotAnalyzable = hasNoResponses || analyzeStatus === 'INVALID_RESPONSE_RATE';
  const meetsPrivacyThreshold =
    numberOfRespondents >= MINIMUM_RESPONSES_FOR_SCORE &&
    ResponseRateThresholds.meetsThreshold(responseRate, expectedParticipantsCount);
  // Backend gir bare score for avsluttede målinger med nok svar. Uten score vises aldri et tall eller en farge.
  const hasResult =
    !isActive && isCompleted && meetsPrivacyThreshold && score !== null && score !== undefined;
  const deadline = SurveyStatusCardUtils.getDeadlineDate(createdAt, deadlineInDays);
  const isPastDeadline = deadline !== null && now >= deadline;

  const status: SurveyListItem['status'] = isNotAnalyzable
    ? { label: t('surveyStatusCard.status.notAnalyzable'), tone: 'danger' }
    : isActive
      ? { label: t('surveyStatusCard.status.ongoing'), tone: 'info' }
      : isCompleted
        ? { label: t('surveyStatusCard.status.completed'), tone: 'success' }
        : { label: t('surveyStatusCard.status.analyzing'), tone: 'warning' };

  const scoreTone = (value: number) => toTone(scoreStatusOf(value));

  const formattedRate = responseRate === 0 ? 0 : responseRate.toFixed(2);
  const closedNote = () =>
    analyzeStatus === 'INVALID_RESPONSE_RATE'
      ? t('surveyStatusCard.messages.lowResponseRate', {
          responseRate: formattedRate,
          minimum: expectedParticipantsCount <= 4 ? '100%' : '75%',
        })
      : hasNoResponses
        ? t('surveyStatusCard.messages.noResponses')
        : t('surveyStatusCard.messages.analysisNotReady');
  const note = isActive
    ? undefined
    : isCompleted
      ? hasResult
        ? undefined
        : t('surveyStatusCard.messages.tooFewResponses')
      : closedNote();

  return {
    key: moduleKey('psychologicalSafety', id),
    id,
    module: 'psychologicalSafety',
    title,
    startDate: new Date(createdAt),
    deadline,
    deadlineInDays,
    isOngoing: isActive,
    responses: { count: numberOfRespondents, expected: expectedParticipantsCount, rate: responseRate },
    status,
    score: hasResult && score !== null ? { value: score, tone: scoreTone(score) } : undefined,
    agreement: (hasResult && agreementOf(standardDeviation)) || undefined,
    note,
    answerPath:
      isActive && ongoing?.id === id ? answerPathFor(Routes.FormSubmission, ongoing.uuid) : undefined,
    analysisRoute: hasResult ? SurveyStatusCardUtils.buildAnalyzeSurveyRoute(teamId, id) : undefined,
    reportRoute: hasResult
      ? buildRoute(Routes.InterpretiveReport, { teamId, surveyId: id })
      : undefined,
    canEditDeadline: isActive && !isPastDeadline && numberOfRespondents < expectedParticipantsCount,
    canDelete: isActive && numberOfRespondents === 0,
  };
};

const workEnvironmentStatusKeys: Record<WorkEnvironmentSurveyStatus, string> = {
  [WorkEnvironmentSurveyStatus.DRAFT]: 'draft',
  [WorkEnvironmentSurveyStatus.SURVEY_SENT]: 'sent',
  [WorkEnvironmentSurveyStatus.SURVEY_IN_PROGRESS]: 'ongoing',
  [WorkEnvironmentSurveyStatus.AWAITING_RISK_CALC]: 'awaitingRiskCalc',
  [WorkEnvironmentSurveyStatus.RISK_CALCULATED]: 'completed',
  [WorkEnvironmentSurveyStatus.RISK_ASSESSED]: 'riskAssessed',
  [WorkEnvironmentSurveyStatus.ACTION_PROPOSAL_OPEN]: 'actionProposalOpen',
  [WorkEnvironmentSurveyStatus.ACTION_VOTING_OPEN]: 'actionVotingOpen',
  [WorkEnvironmentSurveyStatus.ACTION_PLAN_PENDING]: 'actionPlanPending',
  [WorkEnvironmentSurveyStatus.ACTION_PLAN_COMPLETED]: 'actionPlanCompleted',
  [WorkEnvironmentSurveyStatus.EVALUATING]: 'evaluating',
  [WorkEnvironmentSurveyStatus.INSUFFICIENT_RESPONSES]: 'insufficientResponses',
  [WorkEnvironmentSurveyStatus.ARCHIVED]: 'archived',
};

const workEnvironmentTone = (status: WorkEnvironmentSurveyStatus): Tone => {
  switch (status) {
    case WorkEnvironmentSurveyStatus.RISK_CALCULATED:
    case WorkEnvironmentSurveyStatus.RISK_ASSESSED:
    case WorkEnvironmentSurveyStatus.ACTION_PLAN_COMPLETED:
      return 'success';
    case WorkEnvironmentSurveyStatus.INSUFFICIENT_RESPONSES:
      return 'warning';
    case WorkEnvironmentSurveyStatus.DRAFT:
    case WorkEnvironmentSurveyStatus.ARCHIVED:
      return 'neutral';
    default:
      return 'info';
  }
};

export const fromWorkEnvironment = (survey: WorkEnvironmentSurvey): SurveyListItem => {
  const { id, uuid, title, createdAt, deadlineInDays, isActive, status, responseRate } = survey;
  const expected = survey.expectedParticipantsCount || null;
  const statusKey = isActive ? 'ongoing' : (workEnvironmentStatusKeys[status] ?? status);

  return {
    key: moduleKey('workEnvironment', id),
    id,
    module: 'workEnvironment',
    title,
    startDate: new Date(createdAt),
    deadline: SurveyDeadlineCalculator.calculateDeadline(createdAt, deadlineInDays),
    isOngoing: isActive,
    // Listen har bare svarprosent, så antallet regnes ut fra forventet antall deltakere.
    responses: {
      count: expected ? Math.round((responseRate / 100) * expected) : null,
      expected,
      rate: responseRate,
    },
    status: {
      label: i18n.t(`surveys.surveyStatuses.${statusKey}`),
      tone: isActive ? 'info' : workEnvironmentTone(status),
    },
    answerPath: isActive ? answerPathFor(Routes.WorkEnvironmentFormSubmission, uuid) : undefined,
    detailsRoute: buildRoute(Routes.DashboardWorkEnvironmentSurvey, { surveyId: id }),
    canEditDeadline: false,
    canDelete: false,
  };
};

export const fromCustom = (survey: CustomSurveyResponse): SurveyListItem => {
  const { id, uuid, title, createdAt, deadlineInDays, isActive, analyzeStatus, templateId } = survey;
  const responseCount = survey.responseCount ?? 0;
  const expected = survey.expectedParticipantsCount;
  const rate = SurveyStatusPresenter.getResponseRateInfo(responseCount, expected).rate;
  const statusInfo = { isActive, analyzeStatus, responseRate: rate, expectedParticipants: expected };

  return {
    key: moduleKey('custom', id),
    id,
    module: 'custom',
    title,
    startDate: new Date(createdAt),
    deadline: SurveyDeadlineCalculator.calculateDeadline(createdAt, deadlineInDays),
    isOngoing: isActive,
    responses: { count: responseCount, expected: expected > 0 ? expected : null, rate },
    status: {
      label: SurveyStatusPresenter.getStatusLabel(statusInfo),
      tone: toTone(SurveyStatusPresenter.getStatusVariant(statusInfo)),
    },
    note: isActive && responseCount === 0 ? i18n.t('surveys.page.waitingForResponses') : undefined,
    answerPath: isActive ? answerPathFor(Routes.CustomSurveyFormSubmission, uuid) : undefined,
    resultsRoute:
      responseCount > 0 && templateId
        ? buildRoute(Routes.DashboardCustomSurveySurvey, { templateId, surveyId: id })
        : undefined,
    canEditDeadline: false,
    canDelete: false,
  };
};

/** Pågående først, deretter nyeste først. */
export const sortSurveys = (items: SurveyListItem[]): SurveyListItem[] =>
  [...items].sort(
    (a, b) =>
      Number(b.isOngoing) - Number(a.isOngoing) || b.startDate.getTime() - a.startDate.getTime(),
  );

export const toPlannedSurveyItems = (
  standardSurveys: PlannedSurvey[] = [],
  customSurveys: ScheduledCustomSurvey[] = [],
): PlannedSurveyListItem[] => {
  const moduleByStandardId = new Map<number, SurveyModule>(
    standardSurveys.map((survey) => [
      survey.id,
      survey.surveyType === 'WORK_ENVIRONMENT' ? 'workEnvironment' : 'psychologicalSafety',
    ]),
  );

  return ScheduledSurveyPresenter.mergeAndSort(standardSurveys, customSurveys).map((survey) => {
    const module = survey.type === 'custom' ? 'custom' : (moduleByStandardId.get(survey.id) ?? 'psychologicalSafety');
    return {
      key: `${survey.type}-${survey.id}`,
      id: survey.id,
      module,
      title: survey.title,
      scheduledTime: new Date(survey.scheduledTime),
      frequencyLabel: ScheduledSurveyPresenter.getFrequencyLabel(survey.frequency),
      editRoute: `${buildRoute(Routes.EditPlannedSurvey, { id: survey.id })}?type=${survey.type}`,
      type: survey.type,
    };
  });
};
